// FinKloz — Black-Scholes Option Pricing
// Implements the Black-Scholes formula for stock option valuation.
// Used for SBC expense calculation under ASPE 3870.
//
// C = S * N(d1) - K * e^(-rT) * N(d2)
// d1 = [ln(S/K) + (r + sigma^2/2) * T] / (sigma * sqrt(T))
// d2 = d1 - sigma * sqrt(T)

// ─── Standard Normal CDF (Abramowitz & Stegun approximation) ───
function normalCDF(x) {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x / 2);
  return 0.5 * (1.0 + sign * y);
}

// ─── Black-Scholes Call Option Price ───
// S: stock price at valuation date
// K: exercise (strike) price
// T: time to expiration in years
// r: risk-free rate (annualized, continuous)
// sigma: volatility (annualized)
export function blackScholesCall(S, K, T, r, sigma) {
  if (T <= 0) return Math.max(S - K, 0); // expired
  if (S <= 0) return 0;
  if (K <= 0) return S; // free option

  const d1 = (Math.log(S / K) + (r + sigma * sigma / 2) * T) / (sigma * Math.sqrt(T));
  const d2 = d1 - sigma * Math.sqrt(T);

  const callPrice = S * normalCDF(d1) - K * Math.exp(-r * T) * normalCDF(d2);
  return Math.round(callPrice * 10000) / 10000; // 4 decimal places
}

// ─── Modification Incremental Value (ASPE 3870) ───
// At modification date, calculate incremental cost per option.
// Both FVs use the SAME stock price (current at modification date).
//
// incrementalPerOption = MAX(FV_modified - FV_original, 0)
//
// S: stock price at modification date
// K_new: new (lower) exercise price
// K_old: old (higher) exercise price
// T: remaining expected life in years
// r: risk-free rate at modification date
// sigma: volatility at modification date
export function modificationIncremental(S, K_new, K_old, T, r, sigma) {
  const fvModified = blackScholesCall(S, K_new, T, r, sigma);
  const fvOriginal = blackScholesCall(S, K_old, T, r, sigma);
  const incremental = Math.max(fvModified - fvOriginal, 0);

  return {
    fvModified: Math.round(fvModified * 10000) / 10000,
    fvOriginal: Math.round(fvOriginal * 10000) / 10000,
    incrementalPerOption: Math.round(incremental * 10000) / 10000,
  };
}

// ─── Batch calculation for multiple cohorts ───
export function calculateCohortFVs(cohorts) {
  return cohorts.map(c => ({
    ...c,
    fairValue: blackScholesCall(c.stockPrice, c.exercisePrice, c.expectedLife, c.riskFreeRate, c.volatility),
  }));
}

// ─── Batch modification calculation ───
export function calculateModificationIncrementals(cohorts, modParams) {
  const { stockPrice, expectedLife, riskFreeRate, volatility } = modParams;

  return cohorts.map(c => {
    const result = modificationIncremental(
      stockPrice, c.newExercisePrice, c.oldExercisePrice,
      expectedLife, riskFreeRate, volatility
    );
    return {
      ...c,
      ...result,
      totalIncremental: Math.round(result.incrementalPerOption * c.numOptions * 100) / 100,
    };
  });
}
